/**
 * Vendor `businessType` is a sub-type (bakery, cafe, pharmacy, …).
 * Platform rules (delivery fee, default GST) use parent categories: food | grocery | other.
 */

export type VendorParentBusinessCategory = 'food' | 'grocery' | 'other';

/** Known onboarding / admin businessType values → parent category */
const VENDOR_BUSINESS_TYPE_TO_PARENT: Readonly<Record<string, VendorParentBusinessCategory>> = {
  food: 'food',
  restaurant: 'food',
  bakery: 'food',
  cafe: 'food',
  beverages: 'food',
  'fast food': 'food',
  dining: 'food',
  grocery: 'grocery',
  groceries: 'grocery',
  pharmacy: 'other',
  retail: 'other',
};

/**
 * Resolve vendor businessType to parent category (food, grocery, or other).
 */
export function resolveVendorParentBusinessCategory(
  businessType: string | undefined | null,
): VendorParentBusinessCategory {
  if (!businessType?.trim()) {
    return 'other';
  }

  const bt = businessType.toLowerCase().trim();
  const exact = VENDOR_BUSINESS_TYPE_TO_PARENT[bt];
  if (exact) {
    return exact;
  }

  if (bt.includes('grocery')) {
    return 'grocery';
  }

  if (
    bt.includes('food') ||
    bt.includes('restaurant') ||
    bt.includes('bakery') ||
    bt.includes('cafe') ||
    bt.includes('beverage') ||
    bt.includes('dining')
  ) {
    return 'food';
  }

  return 'other';
}

/** Delivery fee foodFee / groceryFee; other vendors use base + distance only. */
export function resolveVendorDeliveryFeeCategory(
  businessType: string | undefined | null,
): 'food' | 'grocery' | null {
  const parent = resolveVendorParentBusinessCategory(businessType);
  return parent === 'other' ? null : parent;
}

export function isFoodVendorBusinessType(businessType: string | undefined | null): boolean {
  return resolveVendorParentBusinessCategory(businessType) === 'food';
}

export function isGroceryVendorBusinessType(businessType: string | undefined | null): boolean {
  return resolveVendorParentBusinessCategory(businessType) === 'grocery';
}

/** Multi-vendor order branch: food | grocery | other */
export type VendorOrderBranch = VendorParentBusinessCategory;

export interface VendorOrderBranchCategoryHint {
  id?: string;
  title?: string | { en?: string; te?: string; hi?: string; kn?: string };
  parentCategoryId?: string | null;
  parent?: VendorOrderBranchCategoryHint | null;
}

export function extractCategoryTitleText(
  title: VendorOrderBranchCategoryHint['title'],
): string {
  if (!title) {
    return '';
  }
  if (typeof title === 'string') {
    return title;
  }
  return title.en ?? '';
}

type CategoryIndex = ReadonlyMap<string, VendorOrderBranchCategoryHint>;

/** Product categories are at most 2 levels deep; cap parent walks at 3 to avoid bad-data loops. */
const MAX_CATEGORY_PARENT_WALK_DEPTH = 3;

function toCategoryIndex(
  allCategories?: VendorOrderBranchCategoryHint[] | CategoryIndex,
): CategoryIndex | null {
  if (!allCategories) {
    return null;
  }
  if (Array.isArray(allCategories)) {
    return new Map(allCategories.filter((c) => c.id).map((c) => [c.id!, c]));
  }
  return allCategories;
}

/**
 * Walk parentCategoryId until the root category id is reached.
 * Stops after at most 3 parent hops (categories are at most 2 levels deep).
 */
export function resolveRootCategoryId(
  categoryId: string,
  categoriesById: CategoryIndex | VendorOrderBranchCategoryHint[],
): string {
  const byId = toCategoryIndex(categoriesById);
  if (!byId) {
    return categoryId;
  }

  let current = categoryId.trim();
  const guard = new Set<string>();
  let hops = 0;

  while (current) {
    if (guard.has(current)) {
      return categoryId;
    }
    guard.add(current);

    const node = byId.get(current);
    if (!node?.parentCategoryId?.trim()) {
      return current;
    }

    if (hops >= MAX_CATEGORY_PARENT_WALK_DEPTH) {
      return current;
    }

    const parentId = node.parentCategoryId.trim();
    if (!byId.has(parentId)) {
      return current;
    }
    current = parentId;
    hops++;
  }

  return categoryId;
}

/**
 * Resolve the root category for a vendor category using a flat categories list
 * or a nested parent chain when the flat list is unavailable.
 */
export function resolveRootCategory(
  category: VendorOrderBranchCategoryHint,
  allCategories?: VendorOrderBranchCategoryHint[] | CategoryIndex,
): VendorOrderBranchCategoryHint | null {
  if (!category) {
    return null;
  }

  const byId = toCategoryIndex(allCategories);
  if (byId && category.id) {
    const rootId = resolveRootCategoryId(category.id, byId);
    return byId.get(rootId) ?? category;
  }

  let current: VendorOrderBranchCategoryHint | null | undefined = category;
  const guard = new Set<string>();
  let hops = 0;
  while (current?.parent) {
    const currentId = current.id?.trim();
    if (currentId && guard.has(currentId)) {
      break;
    }
    if (hops >= MAX_CATEGORY_PARENT_WALK_DEPTH) {
      break;
    }
    if (currentId) {
      guard.add(currentId);
    }
    current = current.parent;
    hops++;
  }
  return current ?? category;
}

/**
 * Classify a root category title to food / grocery / other.
 * Returns null when the root title is unmapped (caller should fall back to businessType).
 */
export function classifyRootCategoryBranch(
  rootCategory: VendorOrderBranchCategoryHint,
): VendorOrderBranch | null {
  const title = extractCategoryTitleText(rootCategory.title).toLowerCase();
  if (!title) {
    return null;
  }
  if (title.includes('grocery')) {
    return 'grocery';
  }
  if (title.includes('food') || title.includes('restaurant')) {
    return 'food';
  }
  return null;
}

/**
 * Resolve a vendor to its multi-vendor order branch (food, grocery, or other).
 * Used for labels and business rules (tax, delivery fee); not for multi-vendor mix validation.
 */
export function resolveVendorOrderBranch(
  businessType: string | undefined | null,
  category?: VendorOrderBranchCategoryHint | null,
  allCategories?: VendorOrderBranchCategoryHint[] | CategoryIndex,
): VendorOrderBranch {
  if (category) {
    const root = resolveRootCategory(category, allCategories);
    if (root) {
      const fromRoot = classifyRootCategoryBranch(root);
      if (fromRoot === 'food' || fromRoot === 'grocery') {
        return fromRoot;
      }
    }
  }

  return resolveVendorParentBusinessCategory(businessType);
}

/**
 * Root category id for a vendor's assigned category (walks parentCategoryId).
 * Returns null when the vendor has no category id.
 */
export function resolveVendorRootCategoryId(
  category: VendorOrderBranchCategoryHint | null | undefined,
  allCategories?: VendorOrderBranchCategoryHint[] | CategoryIndex,
): string | null {
  if (!category?.id?.trim()) {
    return null;
  }
  if (allCategories) {
    return resolveRootCategoryId(category.id.trim(), allCategories);
  }
  const root = resolveRootCategory(category);
  return root?.id?.trim() ?? category.id.trim();
}

/**
 * Parent (root) category id for same-category vendor checks.
 * Walks parentCategoryId to the root. Returns null when no category can be resolved.
 */
export function resolveVendorParentCategoryKey(
  businessType: string | undefined | null,
  category: VendorOrderBranchCategoryHint | null | undefined,
  allCategories?: VendorOrderBranchCategoryHint[] | CategoryIndex,
  categoryIdFallback?: string | null,
): string | null {
  const rootCategoryId = resolveVendorRootCategoryId(category, allCategories);
  if (rootCategoryId) {
    return rootCategoryId;
  }

  const fallbackCategoryId = categoryIdFallback?.trim();
  if (fallbackCategoryId && allCategories) {
    return resolveRootCategoryId(fallbackCategoryId, allCategories);
  }

  return null;
}

/**
 * Stable key for multi-vendor mix checks: root category id when available,
 * otherwise businessType branch fallback for vendors without a category assignment.
 */
export function resolveVendorCategoryComparisonKey(
  vendorId: string,
  businessType: string | undefined | null,
  category: VendorOrderBranchCategoryHint | null | undefined,
  allCategories?: VendorOrderBranchCategoryHint[] | CategoryIndex,
  categoryIdFallback?: string | null,
): string {
  const parentKey = resolveVendorParentCategoryKey(
    businessType,
    category,
    allCategories,
    categoryIdFallback,
  );
  if (parentKey) {
    return parentKey;
  }

  return `branch:${resolveVendorOrderBranch(businessType, category, allCategories)}`;
}

/**
 * True when a product's category (walked to root) belongs to the grocery branch.
 */
export function isGroceryCategoryBranch(
  category: VendorOrderBranchCategoryHint | null | undefined,
  allCategories?: VendorOrderBranchCategoryHint[] | CategoryIndex,
): boolean {
  if (!category) {
    return false;
  }
  const root = resolveRootCategory(category, allCategories);
  return classifyRootCategoryBranch(root ?? category) === 'grocery';
}
